import { module as BaseModule } from 'modujs';

/**
 * Register a modujs module whose code is loaded via dynamic import().
 * Dependencies (React/Algolia/Embla/etc.) stay out of the main bundle until
 * this module's data-module element is present and init() runs.
 *
 * @param {() => Promise<{ default: new (options: object) => BaseModule }>} loader
 * @param {{ defer?: boolean, deferTimeout?: number }} [options]
 *   defer: wait for requestIdleCallback (or first pointer/focus) before importing —
 *   useful for header search so Algolia is off the LCP critical path.
 */
export default function lazyModule(loader, { defer = false, deferTimeout = 2500 } = {}) {
  return class LazyModule extends BaseModule {
    constructor(m) {
      super(m);
      this._moduleOptions = m;
      this._started = false;
      this._destroyed = false;
      this._instance = null;
      this._loadPromise = null;
    }

    init() {
      if (defer) {
        this._scheduleDeferredLoad();
        return;
      }

      this._startLoad();
    }

    _scheduleDeferredLoad() {
      const start = () => {
        this._clearDeferListeners();
        this._startLoad();
      };

      // Idle-only (no click-to-load): avoids a first-click race where the
      // real module has not attached its trigger listeners yet.
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        this._idleId = window.requestIdleCallback(start, { timeout: deferTimeout });
      } else if (typeof window !== 'undefined') {
        this._timer = window.setTimeout(start, 1);
      } else {
        start();
      }
    }

    _clearDeferListeners() {
      if (typeof window !== 'undefined' && this._idleId != null && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(this._idleId);
        this._idleId = null;
      }

      if (this._timer != null) {
        window.clearTimeout(this._timer);
        this._timer = null;
      }
    }

    _startLoad() {
      if (this._started || this._destroyed) {
        return this._loadPromise;
      }

      this._started = true;
      this._loadPromise = loader()
        .then((mod) => {
          if (this._destroyed) {
            return null;
          }

          const Module = mod.default;
          this._instance = new Module(this._moduleOptions);
          // Reuse the activeModules map already attached to this wrapper by modujs.
          this._instance.mInit(this.modules);
          this._instance.init();
          return this._instance;
        })
        .catch((error) => {
          console.error('[lazyModule] Failed to load module', error);
          return null;
        });

      return this._loadPromise;
    }

    destroy() {
      this._destroyed = true;
      this._clearDeferListeners();

      if (this._instance && typeof this._instance.destroy === 'function') {
        this._instance.destroy();
        this._instance = null;
        return;
      }

      if (this._loadPromise) {
        this._loadPromise.then((instance) => {
          if (instance && typeof instance.destroy === 'function') {
            instance.destroy();
          }
        });
      }
    }
  };
}
